import { CheckCircle2, Info, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import signCareLogo from '@/assets/brand/signcare-logo.svg';
import mobileVerificationArt from '@/assets/images/mobile-verification.svg';
// The boxed code input, shared with the Email Verification step and its builder preview.
import { OTP_CODE_LENGTH, OtpCells } from '@/components/patterns/OtpCells';
import { getApiErrorMessage } from '@/lib/api/apiError';

import {
  SESSION_ENTRY_OUTCOME,
  type SessionEntryResult,
  useRequestSessionOtp,
  useVerifySessionOtp,
} from '../api/useSessionOtp';

// The entry gate for a workflow's common link: the applicant proves the mobile
// number that identifies their run before anything is created server-side.
//
// Two steps — number, then the 6-digit code. Verification is what creates or
// resumes the session, so `onVerified` receives the result and hands off to the
// run. The validation rules match signcare-auth's OTP login so a number accepted
// in one product is accepted in the other.

const RESEND_SECONDS = 30;
const CODE_LENGTH = OTP_CODE_LENGTH;

/** Same rules as signcare-auth's OtpLoginPage, so the two never disagree. */
function validateMobile(value: string): string | null {
  const v = value.trim();
  if (!v) return 'Enter your mobile number.';
  if (!/^\d+$/.test(v)) return 'Mobile number must contain only digits.';
  if (v.startsWith('91') && v.length > 10) return 'Enter the number without the country code (91).';
  if (v.startsWith('0')) return 'Enter the number without the leading zero.';
  if (!['6', '7', '8', '9'].includes(v.charAt(0)))
    return 'Mobile number must start with 6, 7, 8 or 9.';
  if (v.length !== 10) return 'Mobile number must be exactly 10 digits.';
  return null;
}

/**
 * `9876543210` → `98765*****`, matching the designer's masked subtitle. Shows the
 * first five digits only: enough for the applicant to recognise their own number,
 * without printing it back in full on a shared screen.
 */
function maskMobile(value: string): string {
  const digits = value.trim().replace(/\D/g, '');
  return digits.length === 10 ? `${digits.slice(0, 5)}*****` : digits;
}

export function MobileVerificationGate({
  slug,
  refId,
  inviteToken,
  onVerified,
}: {
  slug: string;
  /** ?ref= label from the link, stored on the session that gets created. */
  refId?: string;
  /**
   * ?i= tracking code from an emailed invite. Attribution only — it does NOT skip
   * this screen; it just lets the server mark that invite as started.
   */
  inviteToken?: string;
  onVerified: (result: SessionEntryResult) => void;
}) {
  const [step, setStep] = useState<'mobile' | 'code'>('mobile');
  const [mobile, setMobile] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const requestOtp = useRequestSessionOtp(slug, refId);
  const verifyOtp = useVerifySessionOtp(slug, refId, inviteToken);
  const busy = requestOtp.isPending || verifyOtp.isPending;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCountdown = () => {
    setCountdown(RESEND_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1 && timerRef.current) clearInterval(timerRef.current);
        return c - 1;
      });
    }, 1000);
  };

  const sendCode = () => {
    const invalid = validateMobile(mobile);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    requestOtp.mutate(mobile.trim(), {
      onSuccess: () => {
        setStep('code');
        setCode('');
        startCountdown();
      },
      // Delivery failures are real here (there's no account to hide), so the
      // applicant is told rather than left waiting for an SMS that never comes.
      onError: (err) => setError(getApiErrorMessage(err)),
    });
  };

  // `override` is the freshly-typed code handed over by the OTP cells. It arrives
  // before the state update that produced it has rendered, so reading `code` here
  // would see only five digits and reject a valid entry. Falling back to `code` keeps
  // the Continue button behaving exactly as before.
  const submitCode = (override?: string) => {
    const entered = override ?? code;
    if (entered.length !== CODE_LENGTH) {
      setError(`Enter the ${CODE_LENGTH}-digit code.`);
      return;
    }
    setError(null);
    verifyOtp.mutate(
      { mobileNumber: mobile.trim(), code: entered },
      {
        onSuccess: onVerified,
        onError: (err) => {
          setError(getApiErrorMessage(err));
          setCode('');
        },
      },
    );
  };

  return (
    // `sc-run-*` are inert outside `.sc-session-preview-page`; inside it they collapse
    // this frame on a phone. `min-h-dvh` is kept for the desktop case and is overridden
    // there. Byte-for-byte the same shell/column/card as SessionMobilePanel and the
    // welcome screen: the card is FULL height with content at the top, not a short box
    // floating in the middle. Previously this screen centred itself (`justify-center`)
    // and sized to its content, so crossing from the gate into PAN made the card jump
    // in both height and position — the flow looked like two different products.
    //
    // No brand logo here either: it belongs to the welcome screen only, exactly as on
    // the step screens, which show "Secured by SignCare" at the bottom instead.
    <div className="sc-run-shell flex h-full w-full flex-col overflow-y-auto bg-[#F1F5FB] px-4 py-8 md:px-8">
      <div className="sc-run-column mx-auto w-full max-w-lg">
        <div className="sc-run-card flex min-h-[calc(100dvh-8rem)] flex-col rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          {/* Designer: `.sc-otp-verification__title` is 22px semibold tracking-tight,
              `__subtitle` is --text-14 at mt-2, and the header block carries mb-8. */}
          <div className="mb-8 text-center">
            <h1
              className="m-0 text-[22px] font-semibold leading-[1.25] tracking-tight"
              style={{ color: 'var(--brand-heading, #041A3A)' }}
            >
              {step === 'mobile' ? 'Mobile Number Verification' : 'OTP Verification'}
            </h1>
            <p
              className="m-0 mt-2 px-1 text-[length:var(--text-14)] leading-snug"
              style={{ color: 'var(--brand-body, #666666)' }}
            >
              {step === 'mobile'
                ? 'Please enter your mobile number to continue'
                : // Masked, as the designer shows it — the applicant only needs enough
                  // to confirm they typed the right number, not the whole thing back.
                  `An OTP has been sent to your mobile number +91 ${maskMobile(mobile)}`}
            </p>
          </div>

          {step === 'mobile' ? (
            <form
              className="flex flex-1 flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                sendCode();
              }}
            >
              {/* The designer's illustration, above the field
                  (`.sc-workflow-preview__id-card-wrapper`, mb-3, rounded-3xl with a
                  #E5E7EB border) — the same slot the other steps put their ID-card
                  mock in, so this screen sits in the same rhythm as the rest. */}
              <div className="mb-3 w-full">
                <img
                  src={mobileVerificationArt}
                  alt=""
                  className="h-auto w-full rounded-3xl border border-[#E5E7EB] object-contain"
                />
              </div>

              <div className="flex flex-col">
                <label
                  htmlFor="applicant-mobile"
                  className="mb-1 block text-[length:var(--text-14)] font-medium text-[#000000]"
                >
                  Mobile Number<span className="ml-0.5 text-red-500">*</span>
                </label>
                {/* One plain input, as the designer has it — the separate "+91" box we
                    had is gone. The country code is implicit: ApplicantMobileNumber
                    normalises to bare national digits and validateMobile below rejects
                    a leading 91 or 0, so the prefix box was decoration that also cost
                    a third of the field's width on a phone. */}
                <input
                  id="applicant-mobile"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={10}
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-3.5 py-2.5 text-[14px] text-[#111827] outline-none transition-all placeholder:text-[#9CA3AF] focus:border-[#0048FF] focus:ring-1 focus:ring-[#0048FF]"
                  placeholder="Enter 10-digit Mobile Number"
                  value={mobile}
                  onChange={(e) => {
                    setMobile(e.target.value.replace(/\D/g, '').slice(0, 10));
                    setError(null);
                  }}
                />
                {error && <span className="mt-1 text-xs text-destructive">{error}</span>}
              </div>
              {/* `mt-auto` drops Continue to the BOTTOM of the card, where it sits on
                  every step screen. It used to sit directly under the field with the
                  rest of the card empty below it, so the button moved position as the
                  applicant crossed from the gate into the run. */}
              <div className="mt-auto pt-4">
                <GateButton busy={busy} label="Continue" busyLabel="Sending code…" />
              </div>
            </form>
          ) : (
            <form
              className="flex flex-1 flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                submitCode();
              }}
            >
              <div className="flex flex-col gap-1.5">
                <OtpCells
                  value={code}
                  onChange={(next) => {
                    setCode(next);
                    setError(null);
                  }}
                  onComplete={submitCode}
                />
                {/* Countdown, right-aligned under the cells (designer:
                    `.sc-otp-verification__timer`). Only while it is running — once it
                    hits zero the Resend link below is the thing to look at. */}
                {/* Right-aligned to the BOXES, not to the card. The row is capped at
                    320px and centred, so `self-end` on the full-width column parked the
                    timer at the card's far edge, disconnected from the cells it counts
                    for. Sharing the same 320px wrapper lines it up with the last box. */}
                {countdown > 0 && (
                  <span className="mx-auto block w-full max-w-[320px] pr-0.5 text-right text-[12px] font-medium leading-none text-[#111827]">
                    {`00:${String(countdown).padStart(2, '0')}`}
                  </span>
                )}
                {error && <span className="text-xs text-destructive">{error}</span>}
                {/* Resend appears ONLY once the countdown has finished — while it runs,
                    the timer above already says how long is left, and showing both
                    said the same thing twice.

                    The designer's OTP mock has no resend at all. Kept here on purpose:
                    theirs is a static preview, ours is the real gate, and an applicant
                    whose SMS never arrives would otherwise be stuck on this screen with
                    no way forward. */}
                {countdown === 0 && (
                  // Centred under the boxes. Left-aligned it read as a stray caption
                  // hanging off the first cell rather than as an action belonging to
                  // the row above it.
                  <span className="mt-3 text-center text-[length:var(--text-13)] text-gray66">
                    Didn’t get it?{' '}
                    <button
                      type="button"
                      onClick={sendCode}
                      disabled={busy}
                      className="font-medium text-primary hover:underline disabled:opacity-50"
                    >
                      Resend code
                    </button>
                  </span>
                )}
              </div>
              {/* Continue at the bottom, as on every step screen, with the escape hatch
                  directly beneath it so the two stay visually paired. */}
              <div className="mt-auto flex flex-col gap-3 pt-4">
                <GateButton busy={busy} label="Continue" busyLabel="Verifying…" />
                <button
                  type="button"
                  onClick={() => {
                    setStep('mobile');
                    setCode('');
                    setError(null);
                  }}
                  className="text-center text-sm font-medium text-primary hover:underline"
                >
                  Use a different number
                </button>
              </div>
            </form>
          )}

          {/* Same footer as every step screen — `mt-auto` pins it to the bottom of the
              full-height card. Always the SignCare mark, never the tenant's: the line
              states who secures the verification. */}
          <div className="mt-auto flex shrink-0 items-center justify-center gap-1.5 pt-4 text-[length:var(--text-10)] text-primary">
            <span>Secured by</span>
            <img
              src={signCareLogo}
              alt="SignCare"
              className="h-3 w-auto max-w-[84px] shrink-0 object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Branded submit button — same token fallbacks as the run's Continue button.
function GateButton({
  busy,
  label,
  busyLabel,
}: {
  busy: boolean;
  label: string;
  busyLabel: string;
}) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="flex w-full items-center justify-center gap-2 border py-3 text-[length:var(--text-16)] font-medium disabled:opacity-60"
      style={{
        backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
        borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
        color: 'var(--brand-btn-text, #fff)',
        borderRadius: 'var(--brand-btn-radius, 8px)',
      }}
    >
      {busy && <Loader2 size={16} className="animate-spin" />}
      {busy ? busyLabel : label}
    </button>
  );
}

/**
 * Shown when the verified number has already been through this workflow — one
 * number gets one run, so there's nothing to continue.
 */
export function AlreadyVerifiedNotice({ status }: { status: string }) {
  // Title and body both follow the status: "already submitted" would misdescribe a
  // run that finished successfully, which is the most common case here.
  const { title, message, tone } =
    status === 'Completed'
      ? {
          title: 'Verification already completed',
          message:
            'This mobile number has already completed this verification. There’s nothing left to do.',
          tone: 'success' as const,
        }
      : status === 'Rejected'
        ? {
            title: 'Verification already submitted',
            message:
              'This mobile number has already been through this verification and it was not approved.',
            tone: 'neutral' as const,
          }
        : status === 'PendingApproval'
          ? {
              title: 'Verification under review',
              message:
                'This mobile number has already submitted this verification. It’s being reviewed — you don’t need to do anything else.',
              tone: 'neutral' as const,
            }
          : {
              title: 'This link is no longer available',
              message:
                'This mobile number already started this verification and the time limit has passed, so it can’t be continued.',
              tone: 'neutral' as const,
            };

  return (
    <div className="sc-run-shell flex min-h-dvh w-full items-center justify-center bg-[#F1F5FB] px-4 py-8">
      <div className="sc-run-column mx-auto w-full max-w-lg">
        <div className="sc-run-card rounded-2xl border border-grayEF bg-white p-8 text-center shadow-sm">
          <span
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
            style={
              tone === 'success'
                ? {
                    backgroundColor:
                      'color-mix(in srgb, var(--brand-status-success, #00A92C) 12%, white)',
                    color: 'var(--brand-status-success, #00A92C)',
                  }
                : { backgroundColor: '#F4F4F4', color: '#666666' }
            }
          >
            {tone === 'success' ? <CheckCircle2 size={26} /> : <Info size={26} />}
          </span>
          <h1
            className="m-0 mb-2 text-xl font-bold"
            style={{ color: 'var(--brand-heading, #041A3A)' }}
          >
            {title}
          </h1>
          <p
            className="m-0 text-sm leading-relaxed"
            style={{ color: 'var(--brand-body, #666666)' }}
          >
            {message}
          </p>
          <p className="m-0 mt-4 text-xs text-gray66">
            If you think this is a mistake, contact whoever sent you this link.
          </p>
        </div>
      </div>
    </div>
  );
}

export { SESSION_ENTRY_OUTCOME };
